/** Product detail pages — dummy content for /our-portfolios/:slug */

import { pageRoute, appRoute, serviceRoute } from "./paths.js";

/**
 * @typedef {object} ProductDetail
 * @property {string} slug
 * @property {string} title
 * @property {{ text: string, highlight: "primary" | "secondary" | null }[]} titleSegments
 * @property {string} category
 * @property {string} [status]
 * @property {string} serviceId
 * @property {string} serviceLabel
 * @property {string} image
 * @property {string} imageAlt
 * @property {string} eyebrow
 * @property {string} headline
 * @property {string} lead
 * @property {string} overview
 * @property {{ label: string, value: string }[]} metrics
 * @property {{ title: string, body: string }[]} capabilities
 * @property {{ step: string, title: string, body: string }[]} timeline
 * @property {string[]} stack
 * @property {string[]} outcomes
 * @property {string[]} related
 */

/** @type {Record<string, Omit<ProductDetail, "slug"> & { slug?: string }>} */
const details = {
  "meridian-freight-fleet-ops": {
    title: "Meridian Freight: Fleet Ops Dashboard",
    titleSegments: [
      { text: "Fleet Ops ", highlight: null },
      { text: "dashboard", highlight: "primary" },
      { text: " for ", highlight: null },
      { text: "Meridian", highlight: "secondary" },
    ],
    category: "Case study",
    status: "Shipped",
    serviceId: "web-development",
    serviceLabel: "Web Development",
    image: "product/case-study-fleet-dashboard-v4.svg",
    imageAlt: "Meridian Freight fleet operations dashboard",
    eyebrow: "Meridian Freight",
    headline: "Real-time dispatch visibility for a 200-truck fleet.",
    lead:
      "Rebuilt a legacy dispatch tool into a live fleet dashboard — route maps, on-time tracking, and driver status in one view.",
    overview:
      "Meridian’s dispatchers were running a 200-truck fleet from phone calls and a brittle desktop tool. We replaced it with a live web dashboard: route maps, on-time tracking, and driver status in a single view so ops can act without switching systems.",
    metrics: [
      { label: "Dispatch calls", value: "−40%" },
      { label: "Average load time", value: "0.9s" },
      { label: "On-time rate", value: "↑ all regions" },
      { label: "Fleet size", value: "200 trucks" },
    ],
    capabilities: [
      {
        title: "Live route maps",
        body: "Dispatchers see every truck, stop, and delay on one map instead of stitching radio updates.",
      },
      {
        title: "On-time tracking",
        body: "ETA drift and missed windows surface before customers call, not after.",
      },
      {
        title: "Driver status",
        body: "Availability, hours, and exception flags sit next to the load so assignment stays honest.",
      },
      {
        title: "One ops view",
        body: "Maps, status, and history live in a single React app — no more tab-hopping between tools.",
      },
    ],
    timeline: [
      { step: "01", title: "Map the dispatch day", body: "Shadowed ops and named the calls, screens, and delays that actually burned time." },
      { step: "02", title: "Replace the legacy shell", body: "Stood up a React/TypeScript dashboard with live vehicle and load state." },
      { step: "03", title: "Wire telemetry", body: "Route maps, on-time signals, and driver status on a Node and PostgreSQL backbone." },
      { step: "04", title: "Cut the phone tree", body: "Rolled out region by region until dispatch calls dropped and on-time rates moved." },
    ],
    stack: ["React", "TypeScript", "Node.js", "PostgreSQL"],
    outcomes: [
      "Dispatch calls down 40%",
      "Average load time 0.9s",
      "On-time rate up across all regions",
    ],
    related: ["harlow-finch-storefront-rebuild", "trailhead-cross-platform-fitness", "nordly-health-design-system"],
  },
  "harlow-finch-storefront-rebuild": {
    title: "Harlow & Finch: Storefront Rebuild",
    titleSegments: [
      { text: "Storefront ", highlight: null },
      { text: "rebuild", highlight: "primary" },
      { text: " for ", highlight: null },
      { text: "Harlow", highlight: "secondary" },
    ],
    category: "Case study",
    status: "Shipped",
    serviceId: "web-development",
    serviceLabel: "Web Development",
    image: "product/case-study-storefront-rebuild-v4.svg",
    imageAlt: "Harlow and Finch headless storefront rebuild",
    eyebrow: "Harlow & Finch",
    headline: "Headless commerce migration that lifted conversion 22%.",
    lead:
      "Migrated a slow Shopify theme to a headless Next.js storefront with edge-rendered product pages and a streamlined checkout.",
    overview:
      "Harlow & Finch’s theme storefront was slow, hard to iterate, and leaking conversion. We moved catalog and merchandising to a headless Next.js storefront on Shopify Hydrogen — edge-rendered product pages, a tighter checkout, and a performance budget the marketing team could actually keep.",
    metrics: [
      { label: "Conversion", value: "+22%" },
      { label: "Page load time", value: "−55%" },
      { label: "Lighthouse", value: "96" },
      { label: "Checkout", value: "Streamlined" },
    ],
    capabilities: [
      {
        title: "Headless storefront",
        body: "Next.js and Hydrogen own the storefront UX while Shopify stays the commerce engine.",
      },
      {
        title: "Edge-rendered PDPs",
        body: "Product pages ship from the edge so campaign traffic doesn’t wait on a theme stack.",
      },
      {
        title: "Checkout that converts",
        body: "Fewer steps, clearer totals, and Stripe in the flow without a theme-era detour.",
      },
      {
        title: "Performance as a feature",
        body: "Tailwind, image discipline, and a 96 Lighthouse score that holds after merch updates.",
      },
    ],
    timeline: [
      { step: "01", title: "Baseline the leak", body: "Measured conversion, theme weight, and the PDP/checkout drop-off." },
      { step: "02", title: "Go headless", body: "Stood up Next.js + Hydrogen with Shopify as the source of truth." },
      { step: "03", title: "Rebuild the path to buy", body: "Edge-rendered product pages and a streamlined Stripe checkout." },
      { step: "04", title: "Lock the vitals", body: "Cut load time 55% and lifted conversion 22% without slowing merch." },
    ],
    stack: ["Next.js", "Shopify Hydrogen", "Tailwind", "Stripe"],
    outcomes: [
      "Conversion +22%",
      "Page load time −55%",
      "Lighthouse performance score 96",
    ],
    related: ["meridian-freight-fleet-ops", "cascade-utilities-field-service", "nordly-health-design-system"],
  },
  "cascade-health-intake-agent": {
    title: "Cascade Health: Intake Agent",
    titleSegments: [
      { text: "Intake ", highlight: null },
      { text: "agent", highlight: "primary" },
      { text: " for ", highlight: null },
      { text: "Cascade", highlight: "secondary" },
    ],
    category: "Orchestration",
    status: "Shipped",
    serviceId: "ai-agentic",
    serviceLabel: "Agentic AI",
    image: "product/case-study-ai-evals.svg",
    imageAlt: "Cascade Health intake agent plan graph and tool-call log",
    eyebrow: "Cascade Health",
    headline: "Multi-step patient intake with tool calling and clear human handoffs.",
    lead:
      "Built an agent that verifies identity, checks insurance, books the slot, and confirms — with retries and a guardrail layer that stops it before it does anything risky.",
    overview:
      "Cascade’s intake team was drowning in identity checks, insurance lookups, and booking calls. We shipped an orchestration agent that plans the run, calls tools in order, retries on flaky systems, and hands off to a human when confidence drops — with every step scored and logged.",
    metrics: [
      { label: "Task completion", value: "94%" },
      { label: "Human handoffs", value: "−62%" },
      { label: "Avg resolution", value: "2m 10s" },
      { label: "Guardrails", value: "Inline" },
    ],
    capabilities: [
      {
        title: "Live plan graph",
        body: "Operators watch identity → insurance → booking → confirm as a graph, not a chat dump.",
      },
      {
        title: "Tool-call log",
        body: "Every verification, eligibility check, and slot booking is typed, timed, and replayable.",
      },
      {
        title: "Guardrail layer",
        body: "Risky actions stop before they fire — PHI mistakes and bad bookings never reach the EHR.",
      },
      {
        title: "Cost tracking",
        body: "Token and tool cost sit on the same run so intake volume doesn’t surprise finance.",
      },
    ],
    timeline: [
      { step: "01", title: "Map intake", body: "Named the identity, insurance, booking, and confirm steps that actually fail." },
      { step: "02", title: "Wire the loop", body: "Planner, tool contracts, retries, and a human handoff when confidence drops." },
      { step: "03", title: "Guard the edge", body: "Policy checks in front of EHR writes and anything that could leak PHI." },
      { step: "04", title: "Score every run", body: "Logged completion, handoffs, and cost until resolution hit 2m 10s." },
    ],
    stack: ["LLM orchestration", "Tool calling", "Guardrails", "Tracing", "EHR APIs"],
    outcomes: [
      "Task completion 94%",
      "Human handoffs down 62%",
      "Average resolution 2m 10s",
    ],
    related: ["vantage-legal-clause-evals", "meridian-freight-fleet-ops", "ferris-cole-checkout-research"],
  },
  "vantage-legal-clause-evals": {
    title: "Vantage Legal: Clause Extraction Evals",
    titleSegments: [
      { text: "Clause ", highlight: null },
      { text: "evals", highlight: "primary" },
      { text: " for ", highlight: null },
      { text: "Vantage", highlight: "secondary" },
    ],
    category: "Quality",
    status: "Shipped",
    serviceId: "ai-agentic",
    serviceLabel: "Agentic AI",
    image: "product/case-study-ai-orchestration.svg",
    imageAlt: "Vantage Legal clause extraction eval dashboard",
    eyebrow: "Vantage Legal",
    headline: "An eval harness that catches regressions before they ship.",
    lead:
      "A nightly suite of 340 test cases scores every model version on contract clause extraction, flags regressions, and blocks auto-deploy until a human signs off.",
    overview:
      "Vantage was upgrading extractors in the dark. We built a nightly eval harness: 340 golden contracts, pass-rate-by-version, assertion logs, and a failure-category breakdown. Auto-deploy stays blocked until a reviewer signs off — so clause quality holds as models change.",
    metrics: [
      { label: "Pass rate", value: "98.2%" },
      { label: "Was (v2.1)", value: "88%" },
      { label: "Cost / 1K runs", value: "−31%" },
      { label: "Regressions caught", value: "2 pre-deploy" },
    ],
    capabilities: [
      {
        title: "Pass-rate by version",
        body: "Bars for every model cut so regressions show up before counsel sees them.",
      },
      {
        title: "Assertion log",
        body: "Each of the 340 cases records expected clause, extracted clause, and why it failed.",
      },
      {
        title: "Failure categories",
        body: "Missed indemnity, wrong governing law, and span errors group so the team knows what to fix.",
      },
      {
        title: "Human sign-off gate",
        body: "Auto-deploy stays blocked until a reviewer clears the nightly suite.",
      },
    ],
    timeline: [
      { step: "01", title: "Gold the contracts", body: "Locked 340 clause fixtures from real Vantage matters." },
      { step: "02", title: "Score every cut", body: "Nightly pass-rate, cost, and assertion logs per model version." },
      { step: "03", title: "Block the bad ones", body: "Two regressions caught and held before they shipped." },
      { step: "04", title: "Cut cost", body: "Trimmed eval spend 31% per 1K runs without dropping the 98.2% pass rate." },
    ],
    stack: ["Eval harness", "Prompt versioning", "CI deploy gates", "Cost telemetry", "Review queue"],
    outcomes: [
      "Pass rate 98.2% (up from 88% at v2.1)",
      "Cost per 1K runs down 31%",
      "2 regressions auto-caught before deploy",
    ],
    related: ["cascade-health-intake-agent", "harlow-finch-storefront-rebuild", "nordly-health-design-system"],
  },
  "trailhead-cross-platform-fitness": {
    title: "Trailhead: Cross-platform Fitness App",
    titleSegments: [
      { text: "Cross-platform ", highlight: null },
      { text: "fitness", highlight: "primary" },
      { text: " for ", highlight: null },
      { text: "Trailhead", highlight: "secondary" },
    ],
    category: "Case study",
    status: "Shipped",
    serviceId: "app-development",
    serviceLabel: "App Development",
    image: "product/case-study-appdev-fitness.svg",
    imageAlt: "Trailhead fitness release dashboard and workout phone screen",
    eyebrow: "Trailhead",
    headline: "An offline-first React Native app syncing across 40K+ daily users.",
    lead:
      "Built a fitness tracking app with wearable integration (Apple Watch, Wear OS, Garmin) and offline-first sync that resolves conflicts automatically when connectivity drops.",
    overview:
      "Trailhead needed a fitness app that felt fast on the trail, not just on Wi-Fi. We shipped a React Native client with Apple Watch, Wear OS, and Garmin hooks, plus an offline-first sync layer that queues workouts, heart-rate samples, and route points — then resolves conflicts automatically when the phone comes back online. Releases go through the same crash and sync gates that caught the last regression, so 40K+ daily users stay on a 99.7% crash-free build.",
    metrics: [
      { label: "Crash-free rate", value: "99.7%" },
      { label: "Sync conflicts", value: "0.02%" },
      { label: "App Store rating", value: "4.8★" },
      { label: "Reviews", value: "12,400" },
    ],
    capabilities: [
      {
        title: "Wearable sync status",
        body: "Apple Watch, Wear OS, and Garmin sessions land in one queue — operators see which device is healthy before users complain.",
      },
      {
        title: "Offline-first workouts",
        body: "Routes, splits, and heart-rate samples write locally, then merge when connectivity returns.",
      },
      {
        title: "Conflict resolution",
        body: "Duplicate samples and split edits resolve automatically — 0.02% conflict rate across 40K+ daily users.",
      },
      {
        title: "Release dashboard",
        body: "Crash trend and wearable health sit on the same board that gates every store build.",
      },
    ],
    timeline: [
      { step: "01", title: "Instrument the trail", body: "Named the workouts, wearables, and dead-zone sync cases that actually broke trust." },
      { step: "02", title: "Ship offline-first", body: "React Native client with a local queue that keeps recording when the radio dies." },
      { step: "03", title: "Wire the wearables", body: "Apple Watch, Wear OS, and Garmin sessions into one conflict-aware sync path." },
      { step: "04", title: "Gate every release", body: "Crash trend and sync health block the store until the last regression stays caught." },
    ],
    stack: ["React Native", "Apple Watch", "Wear OS", "Garmin", "Offline-first sync"],
    outcomes: [
      "Crash-free rate 99.7%",
      "Sync conflict rate 0.02%",
      "4.8★ App Store rating across 12,400 reviews",
    ],
    related: ["cascade-utilities-field-service", "cascade-health-intake-agent", "ferris-cole-checkout-research"],
  },
  "cascade-utilities-field-service": {
    title: "Cascade Utilities: Field Service App",
    titleSegments: [
      { text: "Field service ", highlight: null },
      { text: "app", highlight: "primary" },
      { text: " for ", highlight: null },
      { text: "Cascade", highlight: "secondary" },
    ],
    category: "Case study",
    status: "Shipped",
    serviceId: "app-development",
    serviceLabel: "App Development",
    image: "product/case-study-appdev-fieldservice.svg",
    imageAlt: "Cascade Utilities dispatch dashboard and offline work-order phone screen",
    eyebrow: "Cascade Utilities",
    headline: "An offline-capable field app for technicians working with no signal.",
    lead:
      "Built a native iOS/Android app for utility technicians to complete work orders, capture photos and signatures offline, and sync automatically once back in range.",
    overview:
      "Cascade’s technicians were finishing jobs in dead zones with paper forms and a radio. We shipped a native iOS and Android field app: dispatch assigns the work order, the phone captures photos and signatures with no signal, and sync fires automatically once the truck is back in range. Visit completion time dropped 25%, paper forms disappeared across every district, and sync still holds 99.9% after dead zones.",
    metrics: [
      { label: "Visit completion", value: "−25% time" },
      { label: "Sync success", value: "99.9%" },
      { label: "Paper forms", value: "Eliminated" },
      { label: "Districts", value: "All live" },
    ],
    capabilities: [
      {
        title: "Dispatch dashboard",
        body: "Technician status and completion trend live in one ops view — not a whiteboard and a radio.",
      },
      {
        title: "Offline work orders",
        body: "Photos, signatures, and close-out notes capture on-device when the truck has no signal.",
      },
      {
        title: "Auto-sync in range",
        body: "Queued jobs flush the moment connectivity returns — 99.9% success after dead zones.",
      },
      {
        title: "Paper out of the cab",
        body: "Every district closed the clipboard. Completion time dropped 25% without a new process memo.",
      },
    ],
    timeline: [
      { step: "01", title: "Ride the dead zones", body: "Mapped the jobs, photos, and signatures that failed the moment the radio dropped." },
      { step: "02", title: "Build native offline", body: "iOS and Android clients that treat no-signal as the default, not an error." },
      { step: "03", title: "Wire dispatch", body: "Technician status and completion trend on a board ops can actually staff from." },
      { step: "04", title: "Kill the clipboard", body: "Rolled district by district until paper forms were gone and visit time dropped 25%." },
    ],
    stack: ["Swift", "Kotlin", "Offline queue", "Photo + signature capture", "Background sync"],
    outcomes: [
      "Visit completion time −25%",
      "Sync success rate 99.9% even after dead zones",
      "Paper forms eliminated across all districts",
    ],
    related: ["trailhead-cross-platform-fitness", "harlow-finch-storefront-rebuild", "vantage-legal-clause-evals"],
  },
  "ferris-cole-checkout-research": {
    title: "Ferris & Cole: Checkout Research Sprint",
    titleSegments: [
      { text: "Checkout ", highlight: null },
      { text: "research", highlight: "primary" },
      { text: " for ", highlight: null },
      { text: "Ferris", highlight: "secondary" },
    ],
    category: "Research",
    status: "Shipped",
    serviceId: "ui-ux-design",
    serviceLabel: "UI/UX Design",
    image: "product/case-study-uiux-research.svg",
    imageAlt: "Ferris and Cole checkout research sprint artifacts",
    eyebrow: "Ferris & Cole",
    headline: "Persona cards, emotion-curve maps, and friction that cut scope 30%.",
    lead:
      "A checkout research sprint that surfaced 14 friction points, pulled a quote the room couldn’t ignore, and aligned product, design, and engineering in a single workshop.",
    overview:
      "Ferris & Cole’s checkout was accumulating screens nobody could defend. We ran a research sprint: persona cards, an emotion-curve journey map, tagged findings, and a pulled quote that made the pain obvious. Fourteen friction points later, scope dropped 30% — and alignment happened in one workshop, not a quarter of debates.",
    metrics: [
      { label: "Friction points", value: "14 found" },
      { label: "Scope cut", value: "−30%" },
      { label: "Alignment", value: "1 workshop" },
      { label: "Artifacts", value: "Personas + map" },
    ],
    capabilities: [
      {
        title: "Persona cards",
        body: "Who is actually checking out — and where they stall — on cards the room can argue with.",
      },
      {
        title: "Emotion-curve map",
        body: "The journey plotted as feeling, not screens, so drop-off stops being a guess.",
      },
      {
        title: "Tagged findings",
        body: "Fourteen friction points ranked so engineering didn’t have to design in the dark.",
      },
      {
        title: "A pulled quote",
        body: "One customer sentence that ended the debate and locked the workshop in a day.",
      },
    ],
    timeline: [
      { step: "01", title: "Talk to buyers", body: "Interviews that filled persona cards instead of opinions." },
      { step: "02", title: "Map the feeling", body: "Emotion curve across browse → cart → pay." },
      { step: "03", title: "Tag the friction", body: "Fourteen points ranked; 30% of scope left the board." },
      { step: "04", title: "Align in a day", body: "One workshop. Quote on the wall. Checkout path agreed." },
    ],
    stack: ["Interviews", "Journey mapping", "Persona synthesis", "Usability notes", "Workshop facilitation"],
    outcomes: [
      "14 friction points found",
      "Scope cut 30%",
      "Alignment in one workshop",
    ],
    related: ["nordly-health-design-system", "harlow-finch-storefront-rebuild", "trailhead-cross-platform-fitness"],
  },
  "nordly-health-design-system": {
    title: "Nordly Health: Design System Craft",
    titleSegments: [
      { text: "Design system ", highlight: null },
      { text: "craft", highlight: "primary" },
      { text: " for ", highlight: null },
      { text: "Nordly", highlight: "secondary" },
    ],
    category: "Craft",
    status: "Shipped",
    serviceId: "ui-ux-design",
    serviceLabel: "UI/UX Design",
    image: "product/case-study-uiux-craft.svg",
    imageAlt: "Nordly Health design system component craft",
    eyebrow: "Nordly Health",
    headline: "Components, empty states, and WCAG AA across every screen.",
    lead:
      "A design system with buttons, inputs, cards, empty states, and a type scale — plus a screen preview that survived engineering contact.",
    overview:
      "Nordly’s product UI was drifting: one-off screens, slow handoffs, and accessibility as an afterthought. We crafted a system — component swatches, empty states, type scale — and a preview card engineers could build against. Handoff time dropped 55%, reuse hit 82%, and WCAG AA held across all screens.",
    metrics: [
      { label: "Handoff time", value: "−55%" },
      { label: "Component reuse", value: "82%" },
      { label: "Accessibility", value: "WCAG AA" },
      { label: "Coverage", value: "All screens" },
    ],
    capabilities: [
      {
        title: "Component swatches",
        body: "Buttons, inputs, and cards with states documented — not a Figma graveyard.",
      },
      {
        title: "Empty states",
        body: "First-run and no-data screens designed as product, not leftovers.",
      },
      {
        title: "Type scale",
        body: "Hierarchy that holds in a clinical UI without shouting.",
      },
      {
        title: "Screen preview",
        body: "A composed view engineers could implement once — reuse 82%, handoff −55%.",
      },
    ],
    timeline: [
      { step: "01", title: "Audit the drift", body: "Inventory one-offs and the screens failing AA." },
      { step: "02", title: "Craft the kit", body: "Buttons, inputs, cards, empty states, type scale." },
      { step: "03", title: "Compose a screen", body: "A preview card that proved the system in product context." },
      { step: "04", title: "Handoff clean", body: "Specs that cut handoff time 55% and kept WCAG AA." },
    ],
    stack: ["Figma libraries", "Design tokens", "Component docs", "A11y checks", "Dev handoff"],
    outcomes: [
      "Handoff time −55%",
      "Component reuse 82%",
      "WCAG AA across all screens",
    ],
    related: ["ferris-cole-checkout-research", "meridian-freight-fleet-ops", "trailhead-cross-platform-fitness"],
  },
  "vertex-defense-managed-detection": {
    title: "Vertex Defense: Managed Detection & Response",
    titleSegments: [
      { text: "Managed ", highlight: null },
      { text: "detection", highlight: "primary" },
      { text: " for ", highlight: null },
      { text: "Vertex Defense", highlight: "secondary" },
    ],
    category: "Case study",
    status: "Shipped",
    serviceId: "cyber-security",
    serviceLabel: "Cyber Security",
    image: "product/case-study-cybersecurity-soc.svg",
    imageAlt: "Vertex Defense managed detection and response SOC",
    eyebrow: "Vertex Defense",
    headline: "24/7 threat detection with human analysts in the loop",
    lead:
      "Built a SOC dashboard correlating alerts across endpoints, network, and cloud — with automated containment for known threats and human escalation for the rest.",
    overview:
      "Vertex Defense needed detection that didn’t dump every noisy signal on a tired analyst. We correlated endpoint, network, and cloud alerts into one stream: known threats contain themselves, everything else escalates to a human with context. Mean time to detect dropped 68%, false positives fell 40%, and they missed zero critical alerts in six months.",
    metrics: [
      { label: "MTTD", value: "−68%" },
      { label: "False positives", value: "−40%" },
      { label: "Missed critical", value: "0" },
      { label: "Window", value: "6 months" },
    ],
    capabilities: [
      {
        title: "Correlated SOC dashboard",
        body: "Endpoint, network, and cloud alerts in one ranked stream — not three consoles.",
      },
      {
        title: "Automated containment",
        body: "Known threats isolate themselves so analysts spend time on the cases that need judgment.",
      },
      {
        title: "Human escalation",
        body: "Ambiguous incidents land with context, not a raw log dump, for the night desk.",
      },
      {
        title: "Always-on coverage",
        body: "24/7 detection with analysts in the loop — automation where it’s safe, people where it isn’t.",
      },
    ],
    timeline: [
      { step: "01", title: "Map the noise", body: "Named which alerts were known-bad, which needed a human, and who owned each." },
      { step: "02", title: "Correlate the surfaces", body: "Endpoint, network, and cloud into one dashboard the desk would actually open." },
      { step: "03", title: "Wire containment", body: "Known threats auto-contain; the rest escalate with a play attached." },
      { step: "04", title: "Hold the night", body: "MTTD −68%, false positives −40%, zero missed critical alerts in six months." },
    ],
    stack: ["EDR", "SIEM", "Cloud trail", "SOAR playbooks", "PagerDuty"],
    outcomes: [
      "Mean time to detect −68%",
      "False positive rate −40%",
      "0 missed critical alerts in 6 months",
    ],
    related: ["ironclad-bank-vulnerability-management", "cascade-utilities-field-service", "northfield-robotics-embedded"],
  },
  "ironclad-bank-vulnerability-management": {
    title: "Ironclad Bank: Continuous Vulnerability Management",
    titleSegments: [
      { text: "Continuous ", highlight: null },
      { text: "vulnerability", highlight: "primary" },
      { text: " management for ", highlight: null },
      { text: "Ironclad Bank", highlight: "secondary" },
    ],
    category: "Case study",
    status: "Shipped",
    serviceId: "cyber-security",
    serviceLabel: "Cyber Security",
    image: "product/case-study-cybersecurity-vuln.svg",
    imageAlt: "Ironclad Bank continuous vulnerability management board",
    eyebrow: "Ironclad Bank",
    headline: "Continuous scanning and guided remediation that keeps audits boring",
    lead:
      "Replaced annual pentests with continuous scanning, risk-scored findings, and a remediation workflow tied straight into engineering's sprint board.",
    overview:
      "Ironclad Bank was still treating security as an annual event. We replaced the pentest scramble with continuous scanning, risk-scored findings, and tickets that land on engineering’s sprint board. Critical vulns closed in 4 days instead of 45, audit prep time dropped 70%, and nothing carried into the next cycle.",
    metrics: [
      { label: "Critical SLA", value: "4 days" },
      { label: "Was", value: "45 days" },
      { label: "Audit prep", value: "−70%" },
      { label: "Carry-over", value: "0" },
    ],
    capabilities: [
      {
        title: "Continuous scanning",
        body: "Cloud, app, and merge-time checks instead of a once-a-year snapshot.",
      },
      {
        title: "Risk-scored findings",
        body: "Critical work jumps the queue. Noise stays off the sprint board.",
      },
      {
        title: "Guided remediation",
        body: "Each finding becomes an engineering ticket with owner, due date, and evidence.",
      },
      {
        title: "Boring audits",
        body: "Evidence packs assemble from the board — not from a two-week archaeology project.",
      },
    ],
    timeline: [
      { step: "01", title: "Retire the annual scramble", body: "Mapped what the pentest actually found versus what engineering never saw." },
      { step: "02", title: "Turn on continuous scan", body: "Daily cloud, weekly app, and merge-time checks with a single score." },
      { step: "03", title: "Tie to the sprint", body: "Findings became tickets on engineering’s board, not a PDF in email." },
      { step: "04", title: "Make the audit dull", body: "Critical SLA 4 days, audit prep −70%, zero findings carried forward." },
    ],
    stack: ["Nessus", "AWS Inspector", "Jira", "GitHub Actions", "Evidence vault"],
    outcomes: [
      "Critical vulns remediated in 4 days (down from 45)",
      "Audit prep time −70%",
      "Zero findings carried into the next audit cycle",
    ],
    related: ["vertex-defense-managed-detection", "harlow-finch-storefront-rebuild", "nordly-health-design-system"],
  },
  "northfield-robotics-embedded": {
    title: "Northfield Robotics: Embedded Platform Team",
    titleSegments: [
      { text: "Embedded ", highlight: null },
      { text: "platform team", highlight: "primary" },
      { text: " for ", highlight: null },
      { text: "Northfield", highlight: "secondary" },
    ],
    category: "Embedded",
    status: "Shipped",
    serviceId: "staff-augmentation",
    serviceLabel: "Staff Augmentation",
    image: "product/case-study-staffaug-embedded.svg",
    imageAlt: "Northfield Robotics embedded platform team",
    eyebrow: "Northfield Robotics",
    headline: "Four senior engineers embedded in an existing sprint cadence in under two weeks.",
    lead:
      "Staffed a data engineer, two backend engineers, and a DevOps lead directly into Northfield's Jira board and on-call rotation — same standups, same velocity tracking.",
    overview:
      "Northfield needed platform capacity without a hiring cycle. We embedded four seniors — data, two backend, one DevOps — onto their Jira board and on-call rotation in eight days. Same standups, same velocity tracking, same ownership as a full-time hire. Sprint velocity rose 34% and they missed zero on-call rotations.",
    metrics: [
      { label: "Ramp time", value: "8 days" },
      { label: "Sprint velocity", value: "+34%" },
      { label: "Missed on-call", value: "0" },
      { label: "Seats", value: "4 seniors" },
    ],
    capabilities: [
      {
        title: "Role-matched staffing",
        body: "A data engineer, two backend engineers, and a DevOps lead — not a generic bench.",
      },
      {
        title: "Their tools, day one",
        body: "Jira, standups, and on-call from the first week. No parallel process to translate.",
      },
      {
        title: "Velocity that shows up",
        body: "Tracked on their board. Sprint velocity +34% without a separate reporting layer.",
      },
      {
        title: "On-call ownership",
        body: "DevOps joined the rotation. Zero missed pages — the same bar as a full-time hire.",
      },
    ],
    timeline: [
      { step: "01", title: "Match the seats", body: "Data, backend, DevOps — profiles that fit Northfield’s stack and timezone." },
      { step: "02", title: "Land in the workflow", body: "Jira, standups, and on-call access in the first week, not a month of orientation." },
      { step: "03", title: "Ship inside the cadence", body: "PRs on their board, velocity on their charts." },
      { step: "04", title: "Hold the rotation", body: "Eight-day ramp, +34% velocity, zero missed on-call." },
    ],
    stack: ["Jira", "Python", "Go", "Kubernetes", "PagerDuty"],
    outcomes: [
      "Ramp time 8 days",
      "Sprint velocity +34%",
      "Zero missed on-call rotations",
    ],
    related: ["bellcastle-retail-mobile-surge", "cascade-health-intake-agent", "vertex-defense-managed-detection"],
  },
  "bellcastle-retail-mobile-surge": {
    title: "Bellcastle Retail: Mobile Engineering Surge",
    titleSegments: [
      { text: "Mobile ", highlight: null },
      { text: "surge", highlight: "primary" },
      { text: " for ", highlight: null },
      { text: "Bellcastle", highlight: "secondary" },
    ],
    category: "Surge",
    status: "Shipped",
    serviceId: "staff-augmentation",
    serviceLabel: "Staff Augmentation",
    image: "product/case-study-staffaug-mobile-surge.svg",
    imageAlt: "Bellcastle Retail mobile engineering surge",
    eyebrow: "Bellcastle Retail",
    headline: "Scaled a mobile team from 2 to 6 engineers to hit a fixed holiday launch date.",
    lead:
      "Added engineers mid-sprint with weekly capacity reviews, so the client only paid for what they needed and tapered the team back down after launch.",
    overview:
      "Bellcastle had a holiday launch date and a two-person mobile team. We added four engineers mid-sprint, reviewed capacity every week, and tapered seats after ship. They hit the original date, kept a 100% first-pass code review rate, and could flex ±1 seat a month instead of carrying a permanent headcount spike.",
    metrics: [
      { label: "Launch", value: "On date" },
      { label: "Team size", value: "2 → 6" },
      { label: "First-pass reviews", value: "100%" },
      { label: "Capacity flex", value: "±1 / month" },
    ],
    capabilities: [
      {
        title: "Mid-sprint add",
        body: "Engineers joined an in-flight cadence — no freeze, no restart, no extra process.",
      },
      {
        title: "Weekly capacity reviews",
        body: "Seats tracked against the launch plan so Bellcastle only paid for what they needed.",
      },
      {
        title: "Review bar that held",
        body: "100% first-pass code review while the team tripled — quality didn’t become the tax on speed.",
      },
      {
        title: "Taper after launch",
        body: "Capacity adjustable by ±1 seat a month, then down again once the holiday spike passed.",
      },
    ],
    timeline: [
      { step: "01", title: "Lock the date", body: "Holiday launch was fixed. Capacity was the only lever." },
      { step: "02", title: "Surge mid-sprint", body: "Four mobile engineers joined the existing two without resetting the board." },
      { step: "03", title: "Review weekly", body: "Capacity in, capacity out — billed to what the launch actually needed." },
      { step: "04", title: "Ship, then taper", body: "On the original date. Then seats came back down." },
    ],
    stack: ["Swift", "Kotlin", "React Native", "Fastlane", "GitHub"],
    outcomes: [
      "Shipped on the original launch date",
      "100% first-pass code review rate",
      "Capacity adjustable by ±1 seat/month",
    ],
    related: ["northfield-robotics-embedded", "harlow-finch-storefront-rebuild", "nordly-health-design-system"],
  },
};

const PRODUCT_SLUG_ALIASES = {
  "ledgerpoint-multisig-treasury": "vertex-defense-managed-detection",
  "havenmark-nft-provenance": "ironclad-bank-vulnerability-management",
  "aetherbank-identity-hardening": "vertex-defense-managed-detection",
  "harborline-soc-visibility": "ironclad-bank-vulnerability-management",
  "fernbank-analytics-multitenant": "trailhead-cross-platform-fitness",
  "loomstack-usage-billing": "cascade-utilities-field-service",
};

export function getProductDetail(slug) {
  const requested = String(slug || "").toLowerCase();
  const key = PRODUCT_SLUG_ALIASES[requested] || requested;
  const detail = details[key];
  if (!detail) return null;
  return { slug: key, ...detail };
}

export function getAllProductDetails() {
  return Object.keys(details).map((slug) => getProductDetail(slug));
}

export function productDetailRoute(slug, section = "") {
  return pageRoute(`our-portfolios/${slug}`, section);
}

export function productDetailServiceHref(detail) {
  return serviceRoute(detail.serviceId);
}

export function productDetailSignupHref() {
  return pageRoute("signup");
}

export function productDetailHomeHref() {
  return pageRoute("our-portfolios");
}

export function productDetailContactHref() {
  return appRoute("contact");
}
